/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { Stack, Text } from "@microsoft/shifts-ui-core";
import * as React from "react";

import { ScheduleCalendarTypes } from "sh-models";

import schedulesViewStateStore from "../../schedules/lib/store/store";

import { getCellsProps, getTranslations } from "./ScheduleGroupPeopleCountRow.mappers";
import { getClassNames } from "./ScheduleGroupPeopleCountRow.styles";
import { ScheduleGroupPeopleCountRowProps } from "./ScheduleGroupPeopleCountRowProps";

/**
 * Renders the "Staff per N hours" row for one schedule group.
 * Week view uses 6-hour intervals (4 buckets per day: 12AM, 6AM, 12PM, 6PM).
 * Fortnight view uses 12-hour intervals (2 buckets per day: 12AM, 12PM) to fit 14 days.
 * @param props The properties.
 * @returns The rendered "Staff per N hours" row.
 */
export const ScheduleGroupPeopleCountRow: React.FC<ScheduleGroupPeopleCountRowProps> = props => {
    const cellsProps = getCellsProps(props);
    const classNames = getClassNames();
    const translations = getTranslations();
    const cellClassNames = `${classNames.cell} ${classNames.contentCell}`;
    const { scheduleCalendarType } = schedulesViewStateStore();
    const isFortnightView = scheduleCalendarType === ScheduleCalendarTypes.Fortnight;
    // Fortnight uses 12-hour intervals (2 per day) to fit 14 days; Week uses 6-hour intervals (4 per day).
    const intervalHoursCount = isFortnightView ? "12" : "6";

    return (
        <Stack horizontal>
            <Stack.Item align="center" className={`${classNames.cell} ${classNames.titleCell}`}>
                <Text className={classNames.title} variant="small">
                    {translations.get("staffPerHoursTitle").replace("{count}", intervalHoursCount)}
                </Text>
            </Stack.Item>
            {cellsProps.map(({ children, className, key }) => (
                <Stack.Item align="center" className={className ? `${cellClassNames} ${className}` : cellClassNames} grow key={key}>
                    <Text className={classNames.text} variant="small">
                        {children}
                    </Text>
                </Stack.Item>
            ))}
        </Stack>
    );
};
